import { Card, CardContent, Collapse, Typography } from '@material-ui/core';
import { Check, Info, WarningRounded } from '@material-ui/icons';
import { useFormikEnhanced } from '@superdispatch/forms';
import {
  Color,
  ColorDynamic,
  Column,
  Columns,
  Stack,
  useResponsiveValue,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Alert, Box, TextBox } from '@superdispatch/ui-lab';
import { isEqual, omit } from 'es-toolkit';
import { Form, FormikProvider } from 'formik';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreditCard } from 'shared/errors/PaymentService';
import { ChargebeeResponse } from 'shared/helpers/ChargebeeWeb';
import { formatPlural } from 'shared/helpers/IntlHelpers';
import { filterSearchParams, useQuery } from 'shared/helpers/RoutingHelpers';
import { EncryptIcon } from 'shared/icons/EncryptIcon';
import { SeatBasedPricingBox } from 'shared/modules/subscription/core/SeatBasedPricingBox';
import { billingAddressFormErrorsGetter } from 'shared/modules/subscription/core/SubscriptionUtils';
import { trackSubscriptionEvent } from 'shared/modules/subscription/data/SubscriptionAnalytics';
import {
  useBillingAddress,
  useSubscription,
  useSubscriptionAPI,
} from 'shared/modules/subscription/SubscriptionAPI';
import {
  subscriptionContactSchema,
  subscriptionSchema,
} from 'shared/modules/subscription/SubscriptionDTO';
import { LocationState } from 'shared/modules/subscription/SubscriptionSalesTaxDrawer';
import { LinkAnchor } from 'shared/routing/Links';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { ChargebeeEmptyPlaceholder } from 'shared/ui/ChargebeeEmptyPlaceholder';
import { SubscriptionForm } from '../subscription/SubscriptionForm';
import { SubscriptionContactFields } from 'shared/modules/subscription/core/SubscriptionContactFields';
import { SubscriptionSummary } from './SubscriptionSummary';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import styled from 'styled-components';
import { z } from 'zod';

const PaymentFormLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const PaymentFormColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const StickySummaryColumn = styled(PaymentFormColumn)`
  ${({ theme }) => theme.breakpoints.up('sm')} {
    position: sticky;
    top: 16px;
  }
`;

const validationSchema = toFormikValidationSchema(subscriptionSchema);
const validationSchemaWithContact = toFormikValidationSchema(
  z.intersection(subscriptionSchema, subscriptionContactSchema),
);
const SUBSCRIPTION_CONTACT_FIELDS = [
  'contact_first_name',
  'contact_last_name',
  'contact_email',
] as const;

export function SubscriptionPaymentForm() {
  const navigate = useNavigate();
  const { data: subscriptionBillingAddress } = useBillingAddress();
  const { data: settings } = useCarrierSettings();
  const [{ utm_medium, driver_guid, back_url }] = useQuery();
  const {
    billableSeats,
    subscription: subscriptionDetails,
    isDriverSeat,
  } = useSubscription();

  const clientToken: string = subscriptionDetails?.client_token || '';

  const { cardRef, isChargebeeAvailable, addCreditCard } =
    useCreditCard(clientToken);
  const [shouldEnableReinitialize, setShouldEnableReinitialize] =
    useState(true);
  const { saveBillingAddress, updateSubscriptionContact } =
    useSubscriptionAPI();
  const isMobile = useResponsiveValue(true, false);
  const { addSnackbar } = useSnackbarStack();
  const isSubscriptionContactEnabled = useFlag('ctms_subscription_contact');
  const subscriptionContact = subscriptionDetails?.subscription?.contact;

  const formik = useFormikEnhanced({
    enableReinitialize: shouldEnableReinitialize,
    validateOnBlur: false,
    validationSchema: isSubscriptionContactEnabled
      ? validationSchemaWithContact
      : validationSchema,
    initialValues: {
      zip: subscriptionBillingAddress?.billing_zip || '',
      country: subscriptionBillingAddress?.billing_country || 'US',
      state: subscriptionBillingAddress?.billing_state || '',
      city: subscriptionBillingAddress?.billing_city || '',
      address: subscriptionBillingAddress?.billing_address || '',
      seats_count: billableSeats?.billable_seats || 1,
      contact_first_name: subscriptionContact?.first_name || '',
      contact_last_name: subscriptionContact?.last_name || '',
      contact_email: subscriptionContact?.email || '',
    },
    getFormErrors: (error) => {
      return billingAddressFormErrorsGetter(error);
    },
    onSubmit: async (values) => {
      setShouldEnableReinitialize(false);
      const isBillingChanged = !isEqual(
        omit(values, SUBSCRIPTION_CONTACT_FIELDS),
        omit(formik.initialValues, SUBSCRIPTION_CONTACT_FIELDS),
      );
      if (isBillingChanged) {
        await saveBillingAddress({
          billing_country: values.country,
          billing_zip: values.zip,
          billing_state: values.state,
          billing_address: values.address,
          billing_city: values.city,
        });
      }
      // Compare with the saved contact, not `initialValues`: the form stops
      // reinitializing on submit, so a retry after a declined card would
      // otherwise send the already saved contact again.
      const isContactChanged =
        values.contact_first_name.trim() !== subscriptionContact?.first_name ||
        values.contact_last_name.trim() !== subscriptionContact?.last_name ||
        values.contact_email.trim() !== subscriptionContact?.email;
      if (isSubscriptionContactEnabled && isContactChanged) {
        await updateSubscriptionContact({
          first_name: values.contact_first_name.trim(),
          last_name: values.contact_last_name.trim(),
          email: values.contact_email.trim(),
        });
      }
      return addCreditCard({});
    },
    onSubmitFailure: (error) => {
      if (isSubscriptionContactEnabled) {
        addSnackbar(error.message, { variant: 'error' });
      }
    },
    onSubmitSuccess: (responseValues: ChargebeeResponse, values) => {
      const {
        token,
        vaultToken,
        additional_information: { braintree },
      } = responseValues;

      const locationState: LocationState = {
        token,
        vaultToken,
        billingAddress: {
          zip: values.zip,
          country: values.country,
          state: values.state,
        },
        cardDetails: braintree,
        seatsCount: values.seats_count,
      };

      if (formik.dirty) {
        trackSubscriptionEvent({
          name: 'Carrier Clicked Continue to Upgrade in Subscription Details',
          utm_medium,
          carrier_guid: settings?.carrier.guid,
          driver_guid,
        });
      }

      navigate(
        {
          pathname: '/settings/subscription/upgrade_plan',
          search: filterSearchParams({ utm_medium, driver_guid, back_url }),
        },
        { state: locationState },
      );
    },
  });

  if (!subscriptionDetails?.subscription?.plan) {
    return null;
  }

  const billableSeatsCount = Number(billableSeats?.billable_seats);
  const seatsToDecrease = billableSeatsCount - formik.values.seats_count;

  const seatsSection = (
    <>
      <SeatBasedPricingBox
        isMinusDisabled={formik.values.seats_count === 1}
        isPlain={isSubscriptionContactEnabled}
      />
      <Collapse in={formik.values.seats_count < billableSeatsCount}>
        <Box borderRadius="small" backgroundColor="Yellow50" padding="xsmall">
          <Columns space="xsmall">
            <Column width="content">
              <WarningRounded
                htmlColor={Color.Yellow300}
                fontSize={isMobile ? 'small' : 'medium'}
              />
            </Column>
            <Column width="fluid">
              <Stack space="xsmall">
                <Box>
                  <Typography variant="body1">
                    You have {billableSeatsCount} active{' '}
                    {isDriverSeat ? 'drivers' : 'seats'} on your team
                  </Typography>
                  <Typography>
                    To decrease team size, deactivate {seatsToDecrease}{' '}
                    {isDriverSeat
                      ? formatPlural(seatsToDecrease, 'driver', 'drivers')
                      : formatPlural(seatsToDecrease, 'seat', 'seats')}
                    .
                  </Typography>
                </Box>

                <LinkAnchor to="/drivers">Manage Drivers</LinkAnchor>
              </Stack>
            </Column>
          </Columns>
        </Box>
      </Collapse>
    </>
  );

  const cardDetailsSection = (
    <>
      <Stack space="xsmall">
        {isSubscriptionContactEnabled ? (
          <Typography variant="h3">Card Details</Typography>
        ) : (
          <Typography variant="h5" color="textSecondary" gutterBottom={true}>
            Card Details
          </Typography>
        )}
        <Columns space="xxsmall" align="top">
          <Column width="content">
            <EncryptIcon />
          </Column>
          <Column width="fluid">
            <TextBox color="secondary" variant="body">
              We use industry-standard encryption and security protocols to
              safeguard your information. Your card details are not stored on
              our internal servers for your protection.
            </TextBox>
          </Column>
        </Columns>
        <Columns space="xxsmall" align="top">
          <Column width="content">
            <Check fontSize="small" htmlColor={ColorDynamic.Dark100} />
          </Column>
          <Column width="fluid">
            <TextBox color="secondary" variant="body">
              We accept all major credit cards
            </TextBox>
          </Column>
        </Columns>
      </Stack>

      <Stack space="large">
        {isChargebeeAvailable ? (
          <SubscriptionForm ref={cardRef} />
        ) : (
          <ChargebeeEmptyPlaceholder />
        )}

        {!isSubscriptionContactEnabled && formik.status.type === 'rejected' && (
          <Alert
            icon={<Info fontSize="small" htmlColor={ColorDynamic.Red500} />}
            severity="critical"
          >
            {formik.status.payload.message}
          </Alert>
        )}
      </Stack>
    </>
  );

  const summaryCard = (
    <Card>
      <CardContent>
        <SubscriptionSummary
          seatsCount={formik.values.seats_count}
          disabled={seatsToDecrease > 0}
          onContinue={() => {
            void formik.submitForm();
          }}
        />
      </CardContent>
    </Card>
  );

  return (
    <FormikProvider value={formik}>
      <Form>
        {isSubscriptionContactEnabled ? (
          <PaymentFormLayout>
            <PaymentFormColumn>
              <Stack space="small">
                <Card>
                  <CardContent>
                    <Stack space="medium">
                      <Stack space="xxsmall">
                        <Typography variant="h3">Carrier TMS Pro</Typography>
                        <TextBox color="secondary" variant="body">
                          Each person who logs in, driver or dispatcher, uses 1
                          seat.
                        </TextBox>
                      </Stack>
                      {seatsSection}
                    </Stack>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <SubscriptionContactFields hasTitle={true} />
                  </CardContent>
                </Card>
                <Card>
                  <CardContent>
                    <Stack space="medium">{cardDetailsSection}</Stack>
                  </CardContent>
                </Card>
              </Stack>
            </PaymentFormColumn>
            <StickySummaryColumn>{summaryCard}</StickySummaryColumn>
          </PaymentFormLayout>
        ) : (
          <Columns space="small" collapseBelow="tablet">
            <Column>
              <Card>
                <CardContent>
                  <Stack space="medium">
                    <Typography variant="h3">Payment Information</Typography>
                    {seatsSection}
                    {cardDetailsSection}
                  </Stack>
                </CardContent>
              </Card>
            </Column>
            <Column>{summaryCard}</Column>
          </Columns>
        )}
      </Form>
    </FormikProvider>
  );
}
